import { useEffect, useState } from "react";
import { Plus, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const EMPTY = { email: "", name: "", company: "", title: "" };
const EMAIL_OK = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

function DuplicateNotice({ dup }) {
  if (!dup) return null;
  if (dup.exists_in_project) return (
    <div className="flex gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-800" data-testid="add-lead-duplicate-warning">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />Dublett! Denna e-postadress finns redan som lead i detta projekt och kan inte läggas till igen.
    </div>
  );
  if (dup.other_projects.length) return (
    <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800" data-testid="add-lead-other-projects-info">
      <Info className="h-4 w-4 shrink-0" />Finns redan i: {dup.other_projects.join(", ")}. Du kan ändå lägga till den i detta projekt.
    </div>
  );
  return null;
}

export function AddLeadDialog({ slot, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [dup, setDup] = useState(null);

  useEffect(() => {
    const email = f.email.trim();
    if (!EMAIL_OK.test(email)) { setDup(null); return; }
    const t = setTimeout(() => api.get(`/projects/${slot}/leads/check`, { params: { email } }).then((r) => setDup(r.data)).catch(() => setDup(null)), 300);
    return () => clearTimeout(t);
  }, [f.email, slot]);

  const save = async (e) => {
    e.preventDefault();
    if (dup?.exists_in_project) return;
    try {
      await api.post(`/projects/${slot}/leads`, f);
      toast.success("Lead added");
      setF(EMPTY);
      setDup(null);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="lead-add-manual-button"><Plus className="h-4 w-4 mr-1.5" />Add lead</Button>
      </DialogTrigger>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle>Add lead</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {Object.keys(EMPTY).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label className="capitalize">{k}{k === "email" && " *"}</Label>
              <Input type={k === "email" ? "email" : "text"} required={k === "email"} value={f[k]}
                onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`add-lead-${k}-input`}
                className={k === "email" && dup?.exists_in_project ? "border-rose-500 focus-visible:ring-rose-500" : ""} />
              {k === "email" && <DuplicateNotice dup={dup} />}
            </div>
          ))}
          <Button type="submit" className="w-full" disabled={!!dup?.exists_in_project} data-testid="add-lead-submit-button">Save lead</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
